<script setup lang="ts">
import { computed, watch, ref, shallowRef, onUnmounted, reactive } from 'vue';
import router from '@/router';
import {
  AUTO_LOGOUT_TIMER_DURATION_IN_MILLISECONDS,
  useUserStore,
} from '@/stores/user';
import {
  useTimesheetStore,
  EventSources,
  TimesheetErrorResponseStatus,
} from '@/stores/timesheets';
import { useAreaStore } from '@/stores/areas';
import { useRosterStore } from '@/stores/rosters';
import { CoDialog } from '@deputy/copilot2';
import ShiftCard from '@/components/ShiftCard.vue';
import UpcomingShiftsCard from '@/components/UpcomingShiftsCard.vue';
import ShiftStarted from '@/components/ShiftStarted.vue';
import BreakStarted from '@/components/BreakStarted.vue';
import BreakEnded from '@/components/BreakEnded.vue';
import ShiftEnded from '@/components/ShiftEnded.vue';
import EditBreakForm from '@/components/EditBreakForm.vue';
import EditStartTime from '@/components/EditStartTime.vue';
import EditShiftTime from '@/components/EditShiftTime.vue';
import SelectListModalContent from '@/components/SelectListModalContent.vue';
import ShiftQuestionForm from '@/components/ShiftQuestionForm.vue';
import ShiftEndInformation from '@/components/ShiftEndInformation.vue';
import { useAnalyticsService } from '@/lib/util/analytics/useAnalyticsService';
import { useTimesheetSetupStore } from '@/stores/timesheetSetup';
import { DisplayTiming } from '@/lib/request/types/timesheetSetup';
import { getTranslations } from '@/lib/util/i18n';
import type { Slots } from '@/lib/request/types/timesheet';
import { DateTime } from 'luxon';
import { datadogRum } from '@datadog/browser-rum';

const user = useUserStore();
const timesheets = useTimesheetStore();
const timesheetSetup = useTimesheetSetupStore();
const rosters = useRosterStore();
const areas = useAreaStore();

const analytics = useAnalyticsService();
let logoutTimeout: ReturnType<typeof setTimeout>;

const t = getTranslations({
  app: '',
  module: 'views',
  file: 'me-view',
});

timesheets.loadForActiveUser();

interface MeState {
  /** End time entered by the user */
  endTimeUserOverride: number;
  /** Start time entered by the user */
  startTimeUserOverride: number;
}

interface ShiftCardState {
  areaId: number;
}

const state = reactive<MeState>({
  endTimeUserOverride: 0,
  startTimeUserOverride: 0,
});

const shiftCardState = reactive<ShiftCardState>({
  areaId: 0,
});

enum ShiftAction {
  ShiftCard = 'ViewShift',
  StartShift = 'StartShift',
  FillShiftQuestions = 'ShiftQuestions',
  EditShiftQuestions = 'EditShiftQuestions',
  EndShift = 'EndShift',
  StartBreak = 'StartBreak',
  EndBreak = 'EndBreak',
  EditArea = 'EditArea',
  EditStartTime = 'EditShiftStartTime',
  EditBreak = 'EditBreak',
  EditShiftTime = 'EditShiftTime',
}

enum ShiftStatus {
  Starting = 1,
  Complete,
}

const componentForShiftActionMap = {
  [ShiftAction.ShiftCard]: ShiftCard,
  [ShiftAction.StartShift]: ShiftStarted,
  [ShiftAction.FillShiftQuestions]: ShiftStarted,
  [ShiftAction.EndShift]: ShiftEnded,
  [ShiftAction.StartBreak]: BreakStarted,
  [ShiftAction.EndBreak]: BreakEnded,
  [ShiftAction.EditArea]: ShiftCard,
  [ShiftAction.EditStartTime]: ShiftCard,
  [ShiftAction.EditBreak]: ShiftCard,
  [ShiftAction.EditShiftTime]: ShiftEnded,
  [ShiftAction.EditShiftQuestions]: ShiftCard,
};

const shiftAction = ref<ShiftAction>(ShiftAction.ShiftCard);
const shiftActionStatus = ref<ShiftStatus>(ShiftStatus.Starting);
const componentForCompleteShiftAction = shallowRef();

const breakModalTitle = ref<string>(t('edit-modal:title-break'));

function _trackStartBreak(breakType?: string) {
  analytics.track(
    { name: 'SHIFT_BREAK_START_STARTED', screen: 'HOME' },
    {
      source: EventSources.StartBreak,
      shiftBreak: {
        details: timesheets.getShiftActionAnalyticsPayload({
          timesheetId: timesheets.activeTimesheet?.Id,
          breakType,
        }),
      },
    }
  );
}

function _showShiftActionConfirmationModal(shiftActionType?: ShiftAction) {
  if (shiftActionType) {
    shiftAction.value = shiftActionType;
  }
  componentForCompleteShiftAction.value =
    componentForShiftActionMap[shiftAction.value];
  shiftActionStatus.value = ShiftStatus.Complete;
  _startLogoutTimer();
}

function _showShiftActionStartModal(shiftActionType?: ShiftAction) {
  if (shiftActionType) {
    shiftAction.value = shiftActionType;
  }
  shiftActionStatus.value = ShiftStatus.Starting;

  datadogRum.startView(shiftActionType);
}

function startBreak(breakType: string, breakId?: number, source?: string) {
  if (source !== 'selectBreakType') {
    _trackStartBreak(breakType);
  }
  timesheets.startBreak(
    timesheets.activeTimesheetOrThrow.Id,
    breakType,
    breakId,
    source
  );
  _showShiftActionConfirmationModal(ShiftAction.StartBreak);
}

async function startShift(
  areaId: number,
  shiftQuestionsResponses: Record<string, string> = {}
) {
  const currentAreaId = !shiftCardState.areaId ? areaId : shiftCardState.areaId;

  await timesheetSetup.load(DisplayTiming.ClockIn, currentAreaId);

  if (
    timesheetSetup.getAllCustomFields()?.length &&
    !Object.keys(shiftQuestionsResponses).length
  ) {
    showShiftQuestionsModal();
    return;
  }

  timesheets.startShift(currentAreaId, shiftQuestionsResponses);
  _showShiftActionConfirmationModal(ShiftAction.StartShift);
}

function handleSubmitShiftQuestions(
  areaId: number,
  shiftQuestionsResponses: Record<string, string> = {}
) {
  if (timesheets.isOnShift && timesheets.activeTimesheetOrThrow) {
    _submitShiftQuestions(
      timesheets.activeTimesheetOrThrow.Id,
      areaId,
      shiftQuestionsResponses
    );
  } else {
    startShift(
      !shiftCardState.areaId ? areaId : shiftCardState.areaId,
      shiftQuestionsResponses
    );
  }
}

function _submitShiftQuestions(
  timesheetId: number,
  areaId: number,
  shiftQuestionsResponses: Record<string, string> = {}
) {
  timesheets.updateShiftQuestions(timesheetId, areaId, shiftQuestionsResponses);
  shiftAction.value = ShiftAction.ShiftCard;
  shiftActionStatus.value = ShiftStatus.Complete;
}

async function updateArea(areaId: number) {
  await timesheets.updateArea(timesheets.activeTimesheet?.Id, areaId);
  shiftAction.value = ShiftAction.ShiftCard;
  shiftActionStatus.value = ShiftStatus.Complete;
  timesheets.loadForActiveUser();
}

function areaSelected(areaId: number) {
  if (shiftAction.value == ShiftAction.EditArea && timesheets.activeTimesheet) {
    updateArea(areaId);
  } else if (
    shiftAction.value == ShiftAction.EditArea &&
    !timesheets.activeTimesheet
  ) {
    shiftCardState.areaId = areaId;
    closeModal();
  } else {
    startShift(areaId);
  }
}

function updateBreak(breaksSlot?: Slots[]) {
  timesheets.updateBreak(timesheets.activeTimesheet?.Id, breaksSlot);
  shiftAction.value = ShiftAction.ShiftCard;
  shiftActionStatus.value = ShiftStatus.Complete;
}

function updateStartTime(shiftStartTime?: string) {
  timesheets.updateShift(timesheets.activeTimesheet?.Id, shiftStartTime);
  shiftAction.value = ShiftAction.ShiftCard;
  shiftActionStatus.value = ShiftStatus.Complete;
}

function updateEndTime(shiftStartTime?: string, shiftEndTime?: string) {
  if (shiftStartTime) {
    state.startTimeUserOverride = DateTime.fromISO(shiftStartTime).toMillis();
  }
  if (shiftEndTime) {
    state.endTimeUserOverride = DateTime.fromISO(shiftEndTime).toMillis();
  }
  shiftAction.value = ShiftAction.EndShift;
  shiftActionStatus.value = ShiftStatus.Starting;
}

function reloadShiftCard() {
  if (!timesheets.getUserError()) {
    if (timesheets.errors.startingShiftError) {
      showAreasList();
    } else if (timesheets.errors.startingBreakError) {
      showBreakOptions();
    } else if (timesheets.errors.endingBreakError) {
      timesheets.endBreak(timesheets.activeTimesheetOrThrow.Id);
    } else if (timesheets.errors.endingShiftError && timesheets.canEndShift()) {
      timesheets.endShift(timesheets.activeTimesheetOrThrow.Id);
    } else {
      showShiftDetails();
    }
  } else {
    showShiftDetails();
  }
}

function showEndShiftConfirmation(
  shiftQuestionsResponses: Record<string, string> = {}
) {
  _submitEndShift(
    timesheets.activeTimesheetOrThrow.Id,
    shiftQuestionsResponses
  );
}

function showEndShiftInformation() {
  analytics.track(
    { name: 'SHIFT_END_STARTED', screen: 'HOME' },
    {
      source: EventSources.EndShift,
      shift: {
        details: timesheets.getShiftActionAnalyticsPayload({
          timesheetId: timesheets.activeTimesheet?.Id,
        }),
      },
    }
  );
  _showShiftActionStartModal(ShiftAction.EndShift);
  _checkTimesheetAlreadySubmitted();
}

async function _checkTimesheetAlreadySubmitted() {
  await timesheets.loadForActiveUser();
  if (!timesheets.activeTimesheet) {
    timesheets.setEndingShiftError(
      TimesheetErrorResponseStatus.InactiveTimesheet
    );
    _showShiftActionConfirmationModal(ShiftAction.EndShift);
  }
}

function _submitEndShift(
  timesheetId: number,
  shiftQuestionsResponses: Record<string, string> = {}
) {
  const shiftStartTime = state.startTimeUserOverride
    ? DateTime.fromMillis(state.startTimeUserOverride).toISO()
    : '';
  const shiftEndTime = state.endTimeUserOverride
    ? DateTime.fromMillis(state.endTimeUserOverride).toISO()
    : '';

  timesheets.endShift(
    timesheetId,
    shiftEndTime,
    shiftStartTime,
    shiftQuestionsResponses
  );
  _showShiftActionConfirmationModal(ShiftAction.EndShift);
}

function showEndBreakConfirmation() {
  analytics.track(
    { name: 'SHIFT_BREAK_END_STARTED', screen: 'HOME' },
    {
      source: EventSources.EndBreak,
      shiftBreak: {
        details: timesheets.getShiftActionAnalyticsPayload({
          timesheetId: timesheets.activeTimesheetOrThrow.Id,
        }),
      },
    }
  );
  timesheets.endBreak(timesheets.activeTimesheetOrThrow.Id);
  _showShiftActionConfirmationModal(ShiftAction.EndBreak);
}

function _startLogoutTimer() {
  watch(
    () => timesheets.isProcessingShiftAction == false,
    function () {
      if (logoutTimeout) {
        clearTimeout(logoutTimeout);
      }
      if (
        user.data.activeUser &&
        shiftAction.value != ShiftAction.ShiftCard &&
        shiftActionStatus.value == ShiftStatus.Complete &&
        !timesheets.isProcessingShiftAction &&
        !timesheets.hasAnyErrors
      ) {
        logoutTimeout = setTimeout(async () => {
          if (
            shiftAction.value != ShiftAction.ShiftCard &&
            componentForCompleteShiftAction.value
          ) {
            shiftAction.value = ShiftAction.ShiftCard;
            componentForCompleteShiftAction.value = null;
            logout();
          }
        }, AUTO_LOGOUT_TIMER_DURATION_IN_MILLISECONDS);
      }
    }
  );
}

async function showShiftDetails() {
  shiftAction.value = ShiftAction.ShiftCard;
  clearTimeout(logoutTimeout);
  timesheets.loadForActiveUser();
}

function showEditStartTime() {
  _showShiftActionStartModal(ShiftAction.EditStartTime);
}

function showEditShiftTime() {
  _showShiftActionStartModal(ShiftAction.EditShiftTime);
}

function showEditBreak() {
  _showShiftActionStartModal(ShiftAction.EditBreak);
}

function showAreasList() {
  _showShiftActionStartModal(ShiftAction.StartShift);
}

function showEditAreaList() {
  _showShiftActionStartModal(ShiftAction.EditArea);
}

function showShiftQuestionsModal() {
  _showShiftActionStartModal(ShiftAction.FillShiftQuestions);
}

function showBreakOptions() {
  _showShiftActionStartModal(ShiftAction.StartBreak);
  _trackStartBreak();
}

function updateBreakModalTitle(step: string) {
  breakModalTitle.value = step;
}

const editModalTitle = computed(() => {
  if (shiftAction.value == ShiftAction.EditBreak) {
    return breakModalTitle.value;
  } else if (shiftAction.value == ShiftAction.EditStartTime) {
    return t('edit-modal:title-start-time');
  } else {
    return t('edit-modal:title-end-time');
  }
});

function closeModal() {
  if (shiftActionStatus.value == ShiftStatus.Starting) {
    shiftAction.value = ShiftAction.ShiftCard;
    timesheets.loadForActiveUser();
  }
}

function closeModalWithoutReload() {
  shiftAction.value = ShiftAction.ShiftCard;
}

function closeEditShiftTimeModal() {
  // closing of the modal without refreshing the modals -> to persist the values of fields in the end shift modal
  shiftAction.value = ShiftAction.EndShift;
  shiftActionStatus.value = ShiftStatus.Starting;
}

async function logout() {
  clearTimeout(logoutTimeout);
  user.logout();
  router.push({ name: 'home' });
}

const area = computed(() => {
  let area = null;

  if (timesheets.activeTimesheet) {
    area = areas.getAreaById(timesheets.activeTimesheet.OperationalUnit);

    if (!area && timesheets.activeTimesheet.OperationalUnitObject) {
      area = areas.getAreaByCompanyAndId(
        timesheets.activeTimesheet.OperationalUnitObject.Company,
        timesheets.activeTimesheet.OperationalUnit
      );
    }
  } else if (rosters.getNextRosterForActiveUser()) {
    area = areas.getAreaById(
      !shiftCardState.areaId
        ? rosters.getNextRosterForActiveUser()!.OperationalUnit
        : shiftCardState.areaId
    );
  }

  return area;
});

onUnmounted(() => {
  componentForCompleteShiftAction.value = null;
});
</script>

<template>
  <div class="me-view" v-if="user.data.activeUser">
    <ShiftCard
      v-if="
        user.data.activeUser &&
        (shiftAction == ShiftAction.ShiftCard ||
          shiftAction == ShiftAction.EndShift)
      "
      v-show="shiftAction == ShiftAction.ShiftCard"
      @showBreaks="showBreakOptions"
      @showAreas="showAreasList"
      @showEditArea="showEditAreaList"
      @showEditShift="showEditStartTime"
      @showEditBreak="showEditBreak"
      @startShift="startShift"
      @showEndShiftInformation="showEndShiftInformation"
      @showEndBreakConfirmation="showEndBreakConfirmation"
      @showEditShiftQuestions="showShiftQuestionsModal"
      @startBreak="startBreak"
      @reloadShiftCard="reloadShiftCard"
      :area="area"
    ></ShiftCard>
    <UpcomingShiftsCard
      v-if="
        user.data.activeUser &&
        (shiftAction == ShiftAction.ShiftCard ||
          shiftAction == ShiftAction.EndShift)
      "
      v-show="shiftAction == ShiftAction.ShiftCard"
    />
    <CoDialog
      v-if="
        shiftAction == ShiftAction.StartShift ||
        shiftAction == ShiftAction.StartBreak ||
        shiftAction == ShiftAction.EditArea
      "
      custom-class="select-list-modal"
      width="500px"
      :title="
        shiftAction == ShiftAction.StartBreak &&
        shiftActionStatus == ShiftStatus.Starting
          ? t('select-break')
          : t('select-area')
      "
      :show-close="
        shiftAction == ShiftAction.StartBreak &&
        shiftActionStatus == ShiftStatus.Starting
      "
      :model-value="
        (shiftAction == ShiftAction.StartShift ||
          shiftAction == ShiftAction.StartBreak ||
          shiftAction == ShiftAction.EditArea) &&
        shiftActionStatus == ShiftStatus.Starting
      "
      @close="closeModal"
    >
      <SelectListModalContent
        @startBreak="startBreak"
        @areaSelected="areaSelected"
        :showAreasModal="
          shiftAction == ShiftAction.StartShift &&
          shiftActionStatus == ShiftStatus.Starting
        "
        :showBreakOptionsModal="
          shiftAction == ShiftAction.StartBreak &&
          shiftActionStatus == ShiftStatus.Starting
        "
        :showEditAreasModal="
          shiftAction == ShiftAction.EditArea &&
          shiftActionStatus == ShiftStatus.Starting
        "
      ></SelectListModalContent>
    </CoDialog>
    <CoDialog
      v-if="shiftAction == ShiftAction.FillShiftQuestions"
      custom-class="shift-question-modal"
      width="500px"
      :title="timesheets.isOnShift ? t('edit-shift-questions') : ''"
      :clean-header="!Boolean(timesheets.isOnShift)"
      :show-close="Boolean(timesheets.isOnShift)"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :model-value="
        shiftAction == ShiftAction.FillShiftQuestions &&
        shiftActionStatus == ShiftStatus.Starting
      "
      @close="closeModal"
    >
      <ShiftQuestionForm
        :cancel-button-text="timesheets.isOnShift ? '' : t('cancel')"
        :submit-button-text="
          timesheets.isOnShift ? t('save') : t('start-shift')
        "
        @cancel="closeModal"
        @submit="handleSubmitShiftQuestions"
      />
    </CoDialog>
    <CoDialog
      custom-class="information-modal"
      width="524px"
      clean-header
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :model-value="
        shiftAction == ShiftAction.EndShift &&
        shiftActionStatus == ShiftStatus.Starting
      "
    >
      <ShiftEndInformation
        v-if="user.data.activeUser && timesheets.activeTimesheet"
        @showShiftDetails="showShiftDetails"
        @showEndShiftConfirmation="showEndShiftConfirmation"
        @showEditShift="showEditShiftTime"
        @closeWithoutReload="closeModalWithoutReload"
        :start-time-user-override="state.startTimeUserOverride"
        :end-time-user-override="state.endTimeUserOverride"
      ></ShiftEndInformation>
    </CoDialog>
    <CoDialog
      custom-class="confirmation-modal"
      width="524px"
      clean-header
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :model-value="
        shiftAction != ShiftAction.ShiftCard &&
        shiftActionStatus == ShiftStatus.Complete
      "
    >
      <component
        v-if="user.data.activeUser"
        :is="componentForCompleteShiftAction"
        @showShiftDetails="showShiftDetails"
        @reloadShiftCard="reloadShiftCard"
        @logout="logout"
      />
    </CoDialog>
    <CoDialog
      v-if="
        shiftAction == ShiftAction.EditBreak ||
        shiftAction == ShiftAction.EditStartTime
      "
      :title="editModalTitle"
      custom-class="edit-modal"
      width="524px"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :model-value="
        (shiftAction == ShiftAction.EditStartTime ||
          shiftAction == ShiftAction.EditBreak) &&
        shiftActionStatus == ShiftStatus.Starting
      "
      @close="closeModal"
    >
      <EditBreakForm
        v-if="shiftAction == ShiftAction.EditBreak && user.data.activeUser"
        @updateBreak="updateBreak"
        @updateBreakModalTitle="updateBreakModalTitle"
      ></EditBreakForm>
      <EditStartTime
        v-else-if="
          shiftAction == ShiftAction.EditStartTime && user.data.activeUser
        "
        @updateShift="updateStartTime"
      ></EditStartTime>
    </CoDialog>
    <CoDialog
      v-if="shiftAction == ShiftAction.EditShiftTime"
      :title="editModalTitle"
      custom-class="edit-modal"
      width="524px"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :model-value="
        shiftAction == ShiftAction.EditShiftTime &&
        shiftActionStatus == ShiftStatus.Starting
      "
      @close="closeEditShiftTimeModal"
    >
      <EditShiftTime
        v-if="user.data.activeUser"
        @updateShift="updateEndTime"
      ></EditShiftTime>
    </CoDialog>
  </div>
</template>

<style>
.confirmation-modal,
.information-modal,
.select-list-modal,
.edit-modal,
.shift-question-modal {
  border-radius: 32px !important;
}

.confirmation-modal .co-dialog__body,
.information-modal .co-dialog__body {
  text-align: center;
  display: flex;
  align-items: stretch;
  justify-content: center;
  flex-direction: column;
}

.confirmation-modal .co-dialog__body,
.information-modal .co-dialog__body {
  min-height: 350px;
}

.information-modal .co-dialog__body {
  padding: 32px;
}

.edit-modal .co-dialog__header,
.select-list-modal .co-dialog__header {
  padding-left: 32px;
}

.edit-modal .co-dialog__body,
.select-list-modal .co-dialog__body {
  padding: 16px 32px 32px 32px;
}

.edit-modal .co-dialog__title,
.shift-question-modal .co-dialog__title,
.select-list-modal .co-dialog__title {
  font-weight: 600;
  font-size: 20px;
}

.me-view {
  width: 100%;
}
</style>
